import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  Autocomplete,
  Box,
  Divider,
  Paper,
  Popper,
  TextField,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ApiService } from "../../services/AppService";
import { Address, ResponseObject, ServiceRequest } from "../../classes/main";
import { getToken } from "../../services/HolderService";
import Cookies from "js-cookie";
import "./index.css";
import AddressLocationDialog from "../address/AddressLocationDialog";
import { Loader } from "../genericComponents/MainLoader/Loader";
import { trackEvent } from "@/app/utils/branch";
import { useLoadScript } from "@react-google-maps/api";
import type { Libraries } from "@googlemaps/js-api-loader";
import { usePathname, useRouter } from "next/navigation";

interface GeoJSON {
  coordinates: [number, number];
}

interface AddressDetectLocation {
  id: string;
  name: string;
  geoJSON: GeoJSON;
}
interface LocationDialogProps {
  openDialog: any;
  setCloseDialog: any;
  handleAddressUpdate: any;
  reloadLocationFlag: any;
}
interface AutocompletePrediction {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  terms: Array<{
    offset: number;
    value: string;
  }>;
  types: string[];
}

const API_KEY = "AIzaSyCY519Z3pdrcYUmVhOV8NftANVlYYoVRKQ";

/** Stable styled paper for autocomplete list — do not define inside render */
const AutocompleteListPaper = styled(Paper)(({ theme }) => ({
  "&::-webkit-scrollbar": { display: "none" },
  msOverflowStyle: "none",
  scrollbarWidth: "none",
  width: "100%",
  maxWidth: "100%",
  marginTop: theme.spacing(1),
  maxHeight: "min(40vh, 280px)",
  overflowY: "auto",
}));

const LocationDialog: React.FC<LocationDialogProps> = ({
  openDialog,
  setCloseDialog,
  handleAddressUpdate,
  reloadLocationFlag,
}) => {
  const [location, setLocation] = useState<{
    latitude: number | string | null;
    longitude: number | string | null;
  }>({
    latitude: null,
    longitude: null,
  });

  const [isShaking, setIsShaking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addressDetectLocation, setAddressDetectLocation] = useState<
    AddressDetectLocation | undefined
  >(undefined);
  const [detectLocationFlag, setDetectLocationFlag] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [address, setAddress] = useState<Address>();
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const token = getToken();
  const apiService: ApiService = useMemo(() => new ApiService(), []);
  const libraries = useRef<Libraries>(["places"]);
  const router = useRouter();
  const pathname = usePathname();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: API_KEY,
    libraries: libraries.current,
  });

  useEffect(() => {
    if (isLoaded && window.google) {
      autocompleteService.current =
        new google.maps.places.AutocompleteService();
    }
  }, [isLoaded]);

  const trackServiceNotFound = useCallback(() => {
    trackEvent("SERVICE_NOT_FOUND", null);
  }, []);

  useEffect(() => {
    if (location.latitude != null && location.longitude != null) {
      const serviceRequest: ServiceRequest = new ServiceRequest();
      serviceRequest.latitude = Number.parseFloat(
        location?.latitude.toString()
      );
      serviceRequest.longitude = Number.parseFloat(
        location.longitude.toString()
      );
      apiService
        .post(apiService.checkAvailability, serviceRequest, getToken()!)
        .then((value: unknown) => {
          const res = value as ResponseObject;
          if (res?.status == "success" && res?.object != null) {
            setLoading(true);
            localStorage.setItem(
              "serviceHub",
              btoa(JSON.stringify(res?.object))
            );
            localStorage.setItem(
              "serviceHubSlug",
              btoa(JSON.stringify(res?.object?.slug))
            );
            if (address) {
              localStorage.setItem("address", btoa(JSON.stringify(address)));
            }
            Cookies.set("serviceHub", res?.object?.id, {
              expires: 365,
              secure: true,
              sameSite: "Strict", // or 'Lax', depending on your use case
            });
            Cookies.set("serviceHubSlug", res?.object?.slug, {
              expires: 365,
              secure: true,
              sameSite: "Strict", // or 'Lax', depending on your use case
            });
            setCloseDialog(false);
            handleAddressUpdate();
            if (detectLocationFlag) {
              setAddressDetectLocation(res?.object);
            }
            if (pathname == "/") {
              window.location.reload();
            } else {
              router.push("/");
            }
          } else {
            alert(
              "Opps! We are not available at this location now. Please choose another location."
            );
            setLoading(false);
            trackServiceNotFound();
          }
        });
    }
  }, [location]);

  const getLocation = async () => {
    try {
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(
              (position) => resolve(position),
              (error) => reject(error)
            );
          } else {
            reject(new Error("Geolocation is not supported by this browser."));
          }
        }
      );
      const { latitude, longitude } = position?.coords;
      setLocation({ latitude, longitude });
      const addressAvailable = localStorage.getItem("address");
      if (addressAvailable == null || undefined) {
        setDetectLocationFlag(true);
      }
    } catch (error: any) {
      console.error("Error obtaining location:", error);
      if (error?.code === 1) {
        alert(
          "Location access is required. We need access to your location to provide better service. Please enable location access: Click on the lock icon next to the URL bar. Change the Location setting to Allow."
        );
        setLoading(false);
      } else {
        alert(
          "Unable to retrieve your location. Please allow location access or enter the address manually."
        );
        setLoading(false);
      }
    }
  };

  // useEffect(() => {
  //   if (typeof window !== "undefined") {
  //     try {
  //       const serviceHubSlug = localStorage.getItem("serviceHubSlug");
  //       if (serviceHubSlug && isBase64(serviceHubSlug)) {
  //         const decodedSlug = JSON.parse(atob(serviceHubSlug));
  //         if (!decodedSlug) {
  //           getLocation();
  //         }
  //       } else {
  //         getLocation();
  //       }
  //     } catch (error) {
  //       getLocation();
  //     }
  //   }
  //   setReloadLocationFlag(false);
  // }, [reloadLocationFlag, setReloadLocationFlag]);

  // const isBase64 = (str: string) => {
  //   try {
  //     atob(str);
  //     return true;
  //   } catch {
  //     return false;
  //   }
  // };

  useEffect(() => {
    if (addressDetectLocation) {
      const address: Address = new Address();
      address.placeId = addressDetectLocation?.id;
      address.formattedAddress = addressDetectLocation?.name;
      address.savedAddress = false;
      address.latitude = addressDetectLocation?.geoJSON?.coordinates[0];
      address.longitude = addressDetectLocation?.geoJSON?.coordinates[1];
      localStorage.setItem("address", btoa(JSON.stringify(address)));
      handleAddressUpdate();
      setDetectLocationFlag(false);
    }
  }, [addressDetectLocation, handleAddressUpdate]);

  useEffect(() => {
  try {
    const locationData = localStorage.getItem("location");

    if (!locationData) return;

    const decoded = atob(locationData);
    const coordinates = JSON.parse(decoded);

    if (Array.isArray(coordinates) && coordinates.length >= 2) {
      setLocation({
        latitude: coordinates[1],
        longitude: coordinates[0],
      });
    }

    localStorage.removeItem("location");
  } catch (error) {
    console.error("Invalid location data in localStorage", error);
  }
}, []);

  useEffect(() => {
    const locationData = localStorage.getItem("serviceHub");
    if (locationData == null || locationData == undefined) {
      const serviceRequest: ServiceRequest = new ServiceRequest();
      serviceRequest.latitude = 28.7040592;
      serviceRequest.longitude = 77.10249019999999;
      apiService
        .post(apiService.checkAvailability, serviceRequest, getToken()!)
        .then((value: unknown) => {
          const res = value as ResponseObject;
          if (res.status == "success" && res.object != null) {
            localStorage.setItem(
              "serviceHub",
              btoa(JSON.stringify(res?.object))
            );
            localStorage.setItem(
              "serviceHubSlug",
              btoa(JSON.stringify(res?.object?.slug))
            );
            Cookies.set("serviceHub", res?.object?.id, {
              expires: 365,
              secure: true,
              sameSite: "Strict", // or 'Lax', depending on your use case
            });
            Cookies.set("serviceHubSlug", res?.object?.slug, {
              expires: 365,
              secure: true,
              sameSite: "Strict", // or 'Lax', depending on your use case
            });
            setAddressDetectLocation(res?.object);
          } else {
            alert(
              "Opps! We are not available at this location now. Please choose another location."
            );
            setLoading(false);
          }
        });
    }
  }, [apiService, reloadLocationFlag]);

  const handleShakeAnimation = () => {
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 500);
  };

  const handleCurrentLocation = () => {
    setLoading(true);
    setCloseDialog(false);
    setDetectLocationFlag(true);
    getLocation();
  };

  const getPlacePredictionsAsync = async (input: string) => {
    return new Promise<google.maps.places.AutocompletePrediction[]>(
      (resolve, reject) => {
        autocompleteService.current?.getPlacePredictions(
          { input, componentRestrictions: { country: ["IN", "NP"] } },
          (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
              resolve(predictions || []);
            } else {
              reject(new Error("Failed to fetch predictions"));
            }
          }
        );
      }
    );
  };

  const handleInputChange = async (
    event: React.ChangeEvent<{}>,
    newInputValue: string
  ) => {
    setInputValue(newInputValue);
    try {
      if (autocompleteService.current && newInputValue) {
        const predictions = await getPlacePredictionsAsync(newInputValue);
        setPredictions(predictions);
      }
    } catch (error) {
      console.error(error);
      setPredictions([]);
    }
  };

  const handlePlaceChange = (event: React.ChangeEvent<{}>, newValue: any) => {
    if (!newValue || !newValue?.place_id) return;

    const placesService = new google.maps.places.PlacesService(
      document.createElement("div")
    );

    placesService?.getDetails(
      { placeId: newValue?.place_id },
      (placeDetails: google.maps.places.PlaceResult | null) => {
        if (placeDetails) {
          const address: Address = new Address();
          address.placeId = newValue?.place_id;
          address.formattedAddress = placeDetails?.formatted_address;
          address.savedAddress = false;

          address.latitude = placeDetails?.geometry?.location?.lat();
          address.longitude = placeDetails?.geometry?.location?.lng();
          setAddress(address);
          // localStorage.setItem("address", btoa(JSON.stringify(address)));
          setLocation({
            latitude: placeDetails?.geometry?.location?.lat() ?? null,
            longitude: placeDetails?.geometry?.location?.lng() ?? null,
          });
        }
      }
    );
  };

  const CustomPopper = useCallback((props: any) => {
    return (
      <Popper
        {...props}
        placement="bottom-start"
        style={{
          ...props.style,
          zIndex: 1600,
          width: "min(100%, calc(100vw - 24px))",
          maxWidth: "min(100%, 360px)",
        }}
      >
        {props.children}
      </Popper>
    );
  }, []);

  return (
    <>
      {loading && <Loader />}
      <Dialog
        open={openDialog}
        onClose={handleShakeAnimation}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        className={isShaking ? "shake" : ""}
        role="dialog"
        aria-labelledby="location-dialog-title"
        aria-describedby="location-dialog-description"
        sx={{
          "& .MuiDialog-paper": {
            display: "flex",
            flexDirection: "column",
            width: { xs: "100%", sm: "min(42rem, calc(100% - 32px))" },
            maxWidth: "42rem",
            borderRadius: isMobile ? 0 : "30px",
            overflow: "hidden",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(246,251,248,0.97) 100%)",
            boxShadow: isMobile ? "none" : "0 32px 90px rgba(11, 26, 19, 0.22)",
            maxHeight: isMobile
              ? "100dvh"
              : "min(92dvh, 880px)",
            margin: isMobile ? 0 : "32px auto",
            paddingTop: isMobile ? "env(safe-area-inset-top, 0px)" : 0,
            paddingBottom: isMobile ? "env(safe-area-inset-bottom, 0px)" : 0,
            "& .salontym-location-header": {
              flexGrow: "0 !important",
            },
            "& .salontym-location-actions": {
              flexGrow: "0 !important",
            },
          },
          "& .MuiBackdrop-root": {
            backdropFilter: "blur(8px)",
            background: "rgba(11, 26, 19, 0.34)",
          },
        }}
        PaperProps={{
          sx: {
            position: "relative",
            overflow: "hidden",
            minHeight: 0,
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "-5rem",
            right: "-4rem",
            width: "14rem",
            height: "14rem",
            borderRadius: "999px",
            background:
              "radial-gradient(circle, rgba(223,246,232,0.9) 0%, rgba(223,246,232,0) 72%)",
            pointerEvents: "none",
          }}
        />
        <DialogContent
          className="salontym-location-header"
          sx={{
            display: "flex",
            flex: "0 0 auto",
            flexGrow: "0 !important",
            flexShrink: 0,
            overflow: "visible",
            padding: {
              xs: "1rem 1.25rem 0.5rem !important",
              sm: "1.25rem 1.25rem 1rem !important",
            },
            justifyContent: "left",
            alignItems: "flex-start",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              width: "3rem",
              height: "3rem",
              minWidth: "3rem",
              borderRadius: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(135deg, rgba(223,246,232,1), rgba(255,240,216,1))",
              color: "#145635",
              boxShadow: "0 16px 30px rgba(18,53,36,0.1)",
            }}
          >
            <LocationOnIcon style={{ fontSize: "28px" }} />
          </Box>
          <Box sx={{ paddingLeft: "0.9rem" }}>
            <Typography
              component="div"
              id="location-dialog-title"
              sx={{
                fontFamily: "var(--salontym-font-display)",
                fontSize: { xs: "1.6rem", sm: "2rem" },
                lineHeight: 1.02,
                color: "#102117",
                margin: 0,
              }}
            >
              Choose your location
            </Typography>
            <DialogContentText
              component="div"
              id="location-dialog-description"
              sx={{
                paddingTop: { xs: "0.35rem", sm: "0.5rem" },
                fontSize: "0.96rem",
                lineHeight: 1.6,
                color: "#5e6d63",
                margin: 0,
                marginBottom: "0 !important",
              }}
            >
              Detect your current location or search manually to see which
              SalonTym services are available in your area.
            </DialogContentText>
          </Box>
        </DialogContent>
        <DialogActions
          className="salontym-location-actions"
          sx={{
            /* space-between + column makes the button and field sit at opposite ends of a tall box */
            justifyContent: { xs: "flex-start", sm: "space-between" },
            alignContent: { xs: "flex-start", sm: "stretch" },
            padding: {
              xs: "0 1.25rem 1rem !important",
              sm: "0 1.25rem 1.25rem !important",
            },
            paddingTop: "0 !important",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "stretch",
            gap: { xs: 1.25, sm: 1.5 },
            flex: "0 0 auto",
            flexGrow: "0 !important",
            flexShrink: 0,
            minHeight: 0,
            overflow: "visible",
            "&.MuiDialogActions-root": {
              paddingTop: "0 !important",
            },
            "& > :not(style) ~ :not(style)": {
              marginLeft: { xs: "0px !important", sm: "12px" },
            },
          }}
        >
          <Button
            onClick={handleCurrentLocation}
            variant="contained"
            sx={{
              textTransform: "none",
              padding: "14px 18px",
              background: "linear-gradient(135deg, #f0ca69, #f5d989)",
              boxShadow: "0 18px 30px rgba(240, 202, 105, 0.28)",
              color: "#102117",
              fontWeight: 700,
              borderRadius: "999px",
              textWrap: "nowrap",
              width: "100%",
              maxWidth: { xs: "100%", sm: "12rem" },
              minWidth: { xs: "100%", sm: "auto" },
              "&:hover": {
                background: "linear-gradient(135deg, #f0ca69, #f5d989)",
              },
            }}
          >
            Detect my location
          </Button>
          {isLoaded && (
            <Autocomplete
              freeSolo
              inputValue={inputValue}
              options={predictions}
              onInputChange={handleInputChange}
              getOptionLabel={(option: any) => option.description ?? ""}
              onChange={handlePlaceChange} // Using the corrected handler
              PaperComponent={(props) => <AutocompleteListPaper {...props} />}
              PopperComponent={CustomPopper}
              renderOption={(props: any, option: any) => {
                const { key, ...restProps } = props;
                return (
                  <Box
                    key={option?.place_id}
                    component="li"
                    {...restProps}
                    sx={{
                      flexDirection: "column",
                      width: "100%",
                      float: "left",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: "left",
                        width: "100%",
                        color: "#102117",
                      }}
                    >
                      {option?.description ?? ""}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: "left",
                        width: "100%",
                        fontSize: "11px",
                        color: "#5e6d63",
                      }}
                    >
                      {option?.structured_formatting?.main_text ?? ""}
                    </Typography>
                  </Box>
                );
              }}
            sx={{
              width: "100%",
              flex: { xs: "0 0 auto", sm: 1 },
              minWidth: 0,
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  borderColor: "#1f7a4d",
                },
              "& .MuiOutlinedInput-root": {
                minHeight: "52px",
                borderRadius: "18px",
                background: "rgba(255,255,255,0.9)",
              },
              "& .MuiInputLabel-root": {
                color: "#607066",
              },
            }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search area or locality"
                  placeholder="e.g. Sector 62, Noida"
                />
              )}
            />
          )}
        </DialogActions>
        <Divider sx={{ borderColor: "rgba(18,53,36,0.08)" }} />
        {token && (
          <DialogContent
            className="salontym-location-saved"
            sx={{
              flex: "1 1 auto",
              minHeight: 0,
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              padding: "1rem 1.25rem max(1.25rem, env(safe-area-inset-bottom, 12px)) !important",
            }}
          >
            <AddressLocationDialog
              setCloseDialog={() => {
                setCloseDialog(false);
              }}
              setLocation={setLocation}
              setLoading={() => setLoading(true)}
            />
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};

export default LocationDialog;
